import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { t } from "i18next";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Card,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";

import { theme } from "@/src/theme/theme";

import { notificationAtom } from "@/src/state/notificationAtom";

import {
  OverlayContent,
  RegistrationNumber,
  VehicleImage
} from "@/src/components/atoms";
import {
  AuctionNotesPreview,
  AuctionTimer,
  BidMachineInfo,
  BidRow,
  OurBidInfo
} from "@/src/components/molecules";
import {
  auctionItemFinalOfferSubmittedChipSx,
  auctionOverlayIconBoxSx,
  auctionOverlayIconsContainerSx,
  getAuctionItemQuickAuctionChipSx,
  getAuctionItemTargetPriceChipSx
} from "@/src/components/molecules/auctionOverlayIcon.styles";
import { Favourite } from "@/src/components/molecules/Favourite/Favourite";
import { HideAuctionIconButton } from "@/src/components/molecules/HideAuctionButton";
import UnifiedImageCarousel from "@/src/components/organisms/UnifiedImageCarousel/UnifiedImageCarousel";

import NoteModal from "@/src/pages/dealer/components/NoteModal";
import { checkForUnreadMessages } from "@/src/pages/dealer/utils/auctionDetailsPage";

import { MINIMUM_BID_RAISE } from "@/src/config/baanaConfig";
import { useHoverCapable } from "@/src/hooks/useHoverCapable";
import { useNoteHistory } from "@/src/queries/dealer/crudNotes";
import {
  AuctionState,
  BidState,
  DealerAuctionDTO,
  UserDTO
} from "@/src/types/types";
import {
  calculateRemainingTimeInSeconds,
  resolveKeySpecs,
  resolveLocationDistance,
  resolveMakeModel,
  resolveModelType,
  resolveYearMileage
} from "@/src/utils/auctionItemUtils";
import { getAuctionImageUrls } from "@/src/utils/imageUtils";
import { groupHistoryByAuction } from "@/src/utils/noteUtils";
import { addThousandsSeparator } from "@/src/utils/numberUtils";
import { getGTMVehicleData } from "@/src/utils/vehicleUtils";

import aKatsastusLogoSquare from "@/assets/images/A-Katsastus_logo-square_transparent.webp";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useSetAtom } from "jotai";

interface AuctionItemProps {
  auction?: DealerAuctionDTO;
  user?: UserDTO;
  currentTime?: number;
  onPlaceBid?: () => void;
  onDeactivateBidMachine?: () => void;
  onEditBidMachine?: () => void;
  onQuickRaise?: () => void;
  isLoading?: boolean;
  isEnded?: boolean;
  showHideButton?: boolean;
  onMarkAsSold?: () => void;
  /**
   * The auction ended but is kept rendered in its last live slot instead of
   * being removed. Forces the `isEnded` bidding-disable path and makes the
   * whole card inert - no click-through, no favourite/hide/notes actions -
   * but keeps every field visible (price, bids, final-offer state, etc),
   * just greyed out. Scoped to this one flag so `isEnded`'s existing
   * behavior elsewhere is untouched.
   */
  endedInPlace?: boolean;
}

const AuctionItem = (props: AuctionItemProps) => {
  const {
    auction = null,
    user = null,
    currentTime,
    onPlaceBid,
    onDeactivateBidMachine,
    onEditBidMachine,
    onQuickRaise,
    isLoading,
    isEnded: isEndedProp = false,
    showHideButton = false,
    onMarkAsSold,
    endedInPlace = false
  } = props;
  const isEnded = isEndedProp || endedInPlace;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sendDataToGTM = useGTMDispatch();
  const setNotification = useSetAtom(notificationAtom);
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isHoverCapable = useHoverCapable();
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: noteHistory } = useNoteHistory(
    auction?.id ?? -1,
    previewOpen && !!auction
  );
  const historyGroups = useMemo(
    () => groupHistoryByAuction(noteHistory),
    [noteHistory]
  );

  const handleOpenModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
    setPreviewOpen(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    return () => {
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (!auction?.auctionItem || !user) {
    return null;
  }

  const totalNotesCount =
    (auction.notes?.length ?? 0) + (auction.noteHistoryCount ?? 0);

  const canPreview =
    isHoverCapable && totalNotesCount > 0 && !endedInPlace && !showModal;

  const handleFooterMouseEnter = () => {
    clearTimeout(closeTimerRef.current);
    openTimerRef.current = setTimeout(() => setPreviewOpen(true), 150);
  };

  const handleFooterMouseLeave = () => {
    clearTimeout(openTimerRef.current);
    closeTimerRef.current = setTimeout(() => setPreviewOpen(false), 120);
  };

  const vehicle = auction.auctionItem.vehicle;
  const makeModel: string = resolveMakeModel(auction);
  const modelType: string = resolveModelType(auction);
  const locationDistance = resolveLocationDistance(auction);
  const yearMileage: string = resolveYearMileage(auction);
  const keySpecs: string = resolveKeySpecs(auction);
  const auctionEndTime = auction.endTime ? auction.endTime : "";
  const timeLeft =
    auction && currentTime
      ? calculateRemainingTimeInSeconds(auctionEndTime, currentTime)
      : 0;

  const carouselImages = getAuctionImageUrls(auction, "md");

  const numberOfBids = auction?.numberOfBids ? auction.numberOfBids : 0;

  const highestBidAmount = auction?.highestBid ? auction.highestBid.amount : 0;

  const myHighestBidAmount = auction?.myHighestBid
    ? auction.myHighestBid.amount
    : 0;

  const ourHighestBidAmount = auction?.ourHighestBid
    ? auction.ourHighestBid.amount
    : 0;

  // Check if a final offer was submitted
  const hasSubmittedFinalOffer =
    ((auction?.myHighestBid?.updateReason === "FINAL_OFFER" &&
      auction?.myHighestBid?.state !== BidState.DECLINED &&
      auction?.myHighestBid?.state !== BidState.ACCEPTED) ||
      (auction?.ourHighestBid?.updateReason === "FINAL_OFFER" &&
        auction?.ourHighestBid?.state !== BidState.DECLINED &&
        auction?.ourHighestBid?.state !== BidState.ACCEPTED)) &&
    auction?.auctionState === AuctionState.ENDED;

  const timeLeftToAcceptOffer =
    auction?.offerEndTime && currentTime
      ? calculateRemainingTimeInSeconds(auction.offerEndTime, currentTime)
      : 0;

  const finalOfferTimeLeft =
    auction?.canSubmitFinalOffer && auction?.offerEndTime && currentTime
      ? calculateRemainingTimeInSeconds(auction.offerEndTime, currentTime)
      : 0;

  const handleNavigation = (e) => {
    e.preventDefault();
    navigate(`/dealer/auction/${auction.id}`);
  };

  let timerSideNote = "";
  if (numberOfBids === 0) {
    timerSideNote = t("general.noBids");
  } else if (numberOfBids === 1) {
    timerSideNote = `1 ${t("general.bid")}`;
  } else {
    timerSideNote = `${numberOfBids} ${t("general.bids")}`;
  }

  const isVatDeductible = auction.auctionItem.auctionItemDetails?.vatDeductible;

  const hasUnreadMessages = checkForUnreadMessages(auction, user);

  const myHighestBid = auction?.myHighestBid;

  const getBorderHighlightColor = (): string | null => {
    if (!myHighestBid) {
      return null;
    }
    return myHighestBid.rank === 1
      ? theme.palette.semantic.success.main
      : theme.palette.semantic.error.main;
  };

  const highlightColor = getBorderHighlightColor();
  const finalOfferBorderColor = auction?.canSubmitFinalOffer
    ? theme.palette.primary.main
    : null;
  const borderColor = finalOfferBorderColor || highlightColor;
  const borderStyling = borderColor ? `4px solid ${borderColor}` : "none";
  const cardBorderTopStyling =
    borderColor && hasUnreadMessages ? `4px solid ${borderColor}` : "none";

  const renderUnreadMessageImage = (imageUrl: string) => (
    <VehicleImage
      key={`auction-item-unread-message-image-${imageUrl}`}
      imageUrl={imageUrl}
      sx={{ height: "216px" }}
    />
  );

  const renderImageCarousel = (imageUrls: string[], isMobile: boolean) => {
    if (imageUrls.length === 0) {
      return (
        <VehicleImage
          imageUrl="/assets/images/missing_car.jpg"
          sx={{ height: "216px" }}
        />
      );
    }

    const images = imageUrls.map((url) => ({
      id: url,
      url,
      alt: makeModel + " " + modelType
    }));

    return (
      <Box
        aria-label="vehicle images carousel"
        sx={{
          height: "216px",
          width: "100%",
          position: "relative"
        }}
      >
        <UnifiedImageCarousel
          images={images}
          arrowVisibility={isMobile ? "always" : "onHover"}
          loop={true}
          height="216px"
          containerSx={{
            height: "216px",
            width: "100%",
            borderRadius: "4px 4px 0px 0px"
          }}
          renderImage={(image) => (
            <Box
              onClick={(e) => {
                sendDataToGTM({
                  event: "auction_item_clicked",
                  user_type: "dealer",
                  vehicle_details: getGTMVehicleData(
                    auction?.auctionItem.vehicle
                  )
                });
                handleNavigation(e);
              }}
              sx={{ cursor: "pointer", height: "216px" }}
            >
              <VehicleImage imageUrl={image.url} sx={{ height: "216px" }} />
            </Box>
          )}
        />
      </Box>
    );
  };

  const upperAuctionItemContent = (
    <Box
      sx={{
        width: "288px",
        position: "relative",
        border: hasUnreadMessages ? "none" : borderStyling,
        borderBottom: "none",
        borderRadius: "4px 4px 0px 0px"
      }}
    >
      <Box
        sx={[
          {
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 5,
            display: "flex"
          },
          auctionOverlayIconsContainerSx
        ]}
      >
        {auction.auctionAdditionalInfo &&
          auction.auctionAdditionalInfo.length > 0 && (
            <Tooltip title={t("auctionAdditionalInfo.tooltip")}>
              <IconButton
                aria-label={String(t("auctionAdditionalInfo.tooltip"))}
                sx={[
                  auctionOverlayIconBoxSx,
                  {
                    position: "relative",
                    "&:hover": {
                      backgroundColor: "transparent"
                    }
                  }
                ]}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    backgroundColor: "primary.contrastText"
                  }}
                />
                <InfoIcon
                  sx={{ position: "relative", color: "primary.main" }}
                />
              </IconButton>
            </Tooltip>
          )}
        {showHideButton && <HideAuctionIconButton auction={auction} />}
        <Favourite auction={auction} />
      </Box>
      {hasUnreadMessages && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            backgroundColor: theme.palette.text.secondary,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: theme.palette.common.white,
            textTransform: "uppercase",
            borderRadius: "4px 4px 0px 0px"
          }}
        >
          <EmailIcon
            fontSize="medium"
            sx={{ color: theme.palette.common.white, mb: 0.75 }}
          />
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              width: "80%",
              fontWeight: theme.typography.fontWeightMedium
            }}
          >
            {t("general.hasNewMessage")}
          </Typography>
        </Box>
      )}

      {carouselImages.length > 0 ? (
        <>
          {hasUnreadMessages
            ? renderUnreadMessageImage(carouselImages[0])
            : renderImageCarousel(carouselImages, isMobile)}
        </>
      ) : (
        <VehicleImage
          imageUrl="/assets/images/missing_car.jpg"
          sx={{ height: "216px" }}
        />
      )}
      <OverlayContent position="bottomLeft">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {auction.auctionItem.kuntoraporttiKey && (
            <Box
              sx={{
                width: 38,
                height: 38,
                p: "6px",
                boxSizing: "border-box",
                backgroundColor: theme.palette.brand.katsastus,
                borderRadius: "4px",
                boxShadow: `1px 1px 3px ${theme.palette.action.disabled}`
              }}
            >
              <Box
                component="img"
                src={aKatsastusLogoSquare}
                alt="A-Katsastus"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)"
                }}
              />
            </Box>
          )}
          {vehicle?.regNo && (
            <Tooltip title={t("components.auctionItem.copyRegnoTooltip")}>
              <RegistrationNumber
                registrationNumber={vehicle.regNo}
                onClick={() => {
                  navigator.clipboard
                    .writeText(vehicle.regNo)
                    .then(() => {
                      setNotification({
                        text: `${t("components.auctionItem.copyRegno", {
                          regno: vehicle.regNo
                        })}`,
                        variant: "info"
                      });
                    })
                    .catch((error) => {
                      console.error(
                        "Failed to copy registration number:",
                        error
                      );
                      setNotification({
                        text: `${t("components.auctionItem.copyRegnoError")}`,
                        variant: "error"
                      });
                    });
                }}
                sx={{
                  "&:hover": {
                    boxShadow: theme.shadows[2]
                  }
                }}
              />
            </Tooltip>
          )}
        </Box>
      </OverlayContent>
      {auction.auctionItem.auctionItemDetails?.auctionDuration === 1 && (
        <OverlayContent
          position="bottomRight"
          sx={getAuctionItemQuickAuctionChipSx()}
        >
          {t("components.auctionItem.quickAuction")}
          <ElectricBoltIcon
            sx={{
              fontSize: (muiTheme) => muiTheme.typography.subtitle2.fontSize
            }}
          />
        </OverlayContent>
      )}
      {hasSubmittedFinalOffer && (
        <Box sx={auctionItemFinalOfferSubmittedChipSx}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.common.white,
              fontWeight: theme.typography.fontWeightBold,
              textTransform: "uppercase"
            }}
          >
            {t("dealer.auctionDetailsPage.finalOfferSubmitted")}
          </Typography>
        </Box>
      )}
      {auction.auctionItem.auctionItemDetails?.targetPrice && (
        <Box
          sx={getAuctionItemTargetPriceChipSx(
            hasSubmittedFinalOffer ? "32px" : "0"
          )}
        >
          <Typography variant="caption">
            {t("components.auctionItem.vehicleImageTargetPrice")}:{" "}
            {`${addThousandsSeparator(
              auction.auctionItem.auctionItemDetails.targetPrice
            )}€`}
          </Typography>
          {isVatDeductible && (
            <Tooltip title={t("components.bidRow.vatDeductible.description")}>
              <Typography variant="caption">
                {t("components.bidRow.vatDeductible.title")}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative"
      }}
    >
      {endedInPlace && (
        // `inert` removes its whole subtree from the accessibility tree, not
        // just from interaction - kept off this element so screen readers
        // still announce that the card ended, instead of an ended card
        // silently vanishing from the list for AT users too.
        <Typography sx={visuallyHidden}>
          {t("components.auctionItem.endedInPlace")}: {makeModel} {modelType}
        </Typography>
      )}
      {/* Image section — dimmed when ended, inert to block interaction */}
      <Box
        inert={endedInPlace}
        sx={{
          opacity: endedInPlace ? theme.palette.action.disabledOpacity : 1
        }}
      >
        {!hasUnreadMessages && upperAuctionItemContent}
      </Box>

      {/* Banner lives outside both inert/opacity Boxes so it renders at full
          opacity in normal flow, right below the image area. endedInPlace
          forces isEnded true but its auctionState hasn't actually flipped to
          ENDED, so neither AuctionTimer below renders - this banner fills
          that gap instead, so its height (54px) matches AuctionTimer's
          contained variant to keep total card height consistent. */}
      {endedInPlace && (
        <Box
          sx={{
            backgroundColor: theme.palette.action.disabledBackground,
            height: "54px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.text.primary,
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}
          >
            {t("components.auctionItem.endedInPlace")}
          </Typography>
        </Box>
      )}

      {/* Card section — dimmed when ended, inert to block interaction */}
      <Box
        // `inert` makes the whole subtree (card link, favourite/hide/notes
        // buttons, image carousel, regno copy) unclickable and unfocusable in
        // one place, making an ended-in-place card fully non-interactive
        // without having to gate each control individually.
        inert={endedInPlace}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          opacity: endedInPlace ? theme.palette.action.disabledOpacity : 1
        }}
      >
        <Card
          data-testid="dealer-card"
          component="a"
          href={"/dealer/auction/" + auction.id}
          sx={{
            width: "288px",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            cursor: endedInPlace ? "default" : "pointer",
            height: "100%",
            textDecoration: "none",
            border: borderStyling,
            borderTop: cardBorderTopStyling,
            borderRadius: "0px 0px 4px 4px",
            overflow: "visible",
            "*": {
              cursor: endedInPlace ? "default !important" : "pointer !important"
            }
          }}
          elevation={4}
          onClick={(e) => {
            sendDataToGTM({
              event: "auction_item_clicked",
              user_type: "dealer",
              vehicle_details: getGTMVehicleData(auction?.auctionItem.vehicle)
            });
            handleNavigation(e);
          }}
        >
          {hasUnreadMessages && upperAuctionItemContent}
          {!isEnded && (
            <AuctionTimer
              variant="contained"
              timeLeft={timeLeft}
              sideNote={timerSideNote}
              endTime={auctionEndTime}
            />
          )}
          {isEnded && auction.auctionState === AuctionState.ENDED && (
            <AuctionTimer
              variant="contained"
              timeLeft={timeLeftToAcceptOffer}
              endTime={auction.offerEndTime}
            />
          )}
          {isEnded && auction.canSubmitFinalOffer && (
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                border: "none",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: theme.typography.fontWeightSemibold,
                  color: theme.palette.common.white,
                  textTransform: "uppercase",
                  textAlign: "center"
                }}
              >
                {t("dealer.auctionDetailsPage.finalOfferTitle")}
              </Typography>
              {finalOfferTimeLeft > 0 && auction.offerEndTime ? (
                <AuctionTimer
                  variant="plain"
                  timeLeft={finalOfferTimeLeft}
                  endTime={auction.offerEndTime}
                  sx={{
                    textAlign: "center",
                    width: "100%",
                    color: theme.palette.common.white
                  }}
                />
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.common.white,
                    fontWeight: theme.typography.fontWeightSemibold,
                    textAlign: "center"
                  }}
                >
                  {t("dealer.auctionDetailsPage.finalOfferDeadline", {
                    deadline: auction.offerEndTime
                      ? new Date(auction.offerEndTime).toLocaleString("fi-FI", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })
                      : ""
                  })}
                </Typography>
              )}
            </Box>
          )}

          {onMarkAsSold && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<CheckCircleIcon />}
                sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMarkAsSold();
                }}
              >
                {t("dealer.bulkMarkAsSold.markAsComplete")}
              </Button>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.75,
                  height: "24px"
                }}
              >
                {auction.endTime && (
                  <>
                    <AccessTimeIcon
                      sx={{ fontSize: 14, color: "text.secondary" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        const days = Math.floor(
                          ((currentTime ?? Date.now()) -
                            new Date(auction.endTime).getTime()) /
                            86400000
                        );
                        return days === 0
                          ? t("dealer.bulkMarkAsSold.acceptedToday")
                          : t("dealer.bulkMarkAsSold.acceptedDaysAgo", {
                              count: days
                            });
                      })()}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              p: 2,
              height: "178px",
              overflow: "hidden"
            }}
          >
            {!vehicle ? (
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: "italic"
                  }}
                >
                  {t("components.auctionItem.oldAuction")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: theme.palette.text.secondary
                  }}
                >
                  {t("components.auctionItem.vehicleDataNotAvailable")}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Always rendered (even when empty) so make/model itself
                    reserves a fixed line height and stays in a static position
                    regardless of how much other info exists. Model type below
                    it only takes up space when there is one — otherwise the
                    rest of the info starts right under make/model instead of
                    leaving a blank reserved line. */}
                <Typography
                  variant="h4"
                  data-testid="dealer-card-brand"
                  sx={{
                    marginBottom: 0,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                >
                  {makeModel || " "}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    fontWeight: theme.typography.fontWeightRegular,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {modelType || " "}
                </Typography>
              </>
            )}

            {vehicle && (
              <>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {keySpecs}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    whiteSpace: "nowrap",
                    fontWeight: theme.typography.fontWeightSemibold
                  }}
                  data-testid="dealer-card-year-mileage"
                >
                  {yearMileage || " "}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mt: 0, whiteSpace: "nowrap" }}
                >
                  {locationDistance || " "}
                </Typography>
              </>
            )}

            {!vehicle && (
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {locationDistance || " "}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "13rem",
                pr: 2,
                pb: 1.5,
                pl: 2
              }}
            >
              {!endedInPlace && (
                <BidRow
                  highestBid={highestBidAmount}
                  myHighestBid={myHighestBidAmount}
                  ourHighestBid={ourHighestBidAmount}
                  isEnded={isEnded}
                  myBidRank={myHighestBid?.rank}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: theme.palette.divider,
                    pt: 2
                  }}
                />
              )}

              {auction && <OurBidInfo auction={auction} />}

              {auction.myBidMachine &&
              auction.myBidMachine.maxAmount > highestBidAmount ? (
                <>
                  {!isEnded && (
                    <BidMachineInfo
                      id={auction.id}
                      maxAmount={auction.myBidMachine.maxAmount}
                      guaranteedAmount={
                        auction.myBidMachine.guaranteedAmount ?? undefined
                      }
                      onDeactivateBidMachine={onDeactivateBidMachine}
                      onEditBidMachine={onEditBidMachine}
                    />
                  )}
                </>
              ) : (
                <>
                  {!isEnded && (
                    <Box>
                      {highestBidAmount > 0 &&
                        highestBidAmount !== myHighestBidAmount &&
                        onQuickRaise && (
                          <Button
                            disabled={isLoading}
                            variant="outlined"
                            color="primary"
                            sx={{
                              width: "100%",
                              pt: 0.75,
                              pb: 0.75,
                              pl: 2,
                              pr: 2
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onQuickRaise?.();
                            }}
                          >
                            {t("components.quickRaise.buttonText", {
                              minimumBidRaise: MINIMUM_BID_RAISE
                            })}
                          </Button>
                        )}
                      <Button
                        variant="contained"
                        disabled={isLoading}
                        color="primary"
                        data-testid="dealer-card-offer-btn"
                        sx={{
                          width: "100%",
                          mt: 1,
                          pt: 0.75,
                          pb: 0.75,
                          pl: 2,
                          pr: 2
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onPlaceBid) {
                            onPlaceBid();
                          }
                        }}
                      >
                        {t("components.auctionItem.placeBid")}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "41px",
              backgroundColor: theme.palette.semantic.secondary.hover,
              borderTop: "1px solid",
              borderColor: theme.palette.divider
            }}
            onMouseEnter={canPreview ? handleFooterMouseEnter : undefined}
            onMouseLeave={canPreview ? handleFooterMouseLeave : undefined}
          >
            {totalNotesCount === 0 ? (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, ml: 2 }}
                >
                  {t("components.auctionItem.noNotes")}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  endIcon={<EditOutlinedIcon fontSize="small" />}
                  onClick={handleOpenModal}
                  sx={{
                    ml: "auto",
                    mr: 1,
                    ...theme.typography.linkSmall,
                    color: theme.palette.semantic.secondary.main,
                    textTransform: "none !important",
                    textDecoration: "none !important",
                    "&:hover": {
                      textDecoration: "none !important"
                    }
                  }}
                >
                  {t("components.auctionItem.addNew")}
                </Button>
              </>
            ) : (
              <Button
                variant="text"
                fullWidth
                onClick={handleOpenModal}
                sx={{ textTransform: "none" }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: "10px",
                    backgroundColor: theme.palette.semantic.secondary.main,
                    color: theme.palette.semantic.secondary.contrastText,
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: theme.typography.fontWeightBold,
                    lineHeight: 1
                  }}
                >
                  {totalNotesCount}
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    ml: 1,
                    textTransform: "none"
                  }}
                >
                  {t("components.auctionItem.note", {
                    count: totalNotesCount
                  })}
                </Typography>
              </Button>
            )}

            {previewOpen && totalNotesCount > 0 && (
              <AuctionNotesPreview
                notes={auction.notes}
                historyGroups={historyGroups}
                currentMileage={auction.auctionItem.auctionItemDetails?.mileage}
                mileageUnit={
                  auction.auctionItem.auctionItemDetails?.mileageUnit
                }
                onShowAll={() => {
                  setPreviewOpen(false);
                  setShowModal(true);
                }}
              />
            )}
          </Box>
        </Card>
        {showModal && (
          <NoteModal
            auction={auction}
            notes={auction.notes}
            isOpen={showModal}
            onClose={handleCloseModal}
          />
        )}
      </Box>
    </Box>
  );
};

export default AuctionItem;
